import React, { useEffect, useRef, useState } from 'react';
import { BaseFileUpload, CombinedRefContent } from '../FileUpload/BaseFileUpload';
import { Icons } from '../../../../components/atoms/SVG';
import { ButtonType } from '../../../../types/ButtonType';
import { ImagePreview, PreviewSuspense, SelectedImagePreview } from '../../../components/ImageUpload';
import { isValid, SelectedImage, useValidateImage } from '../../../FileUpload/utils/validateImage';
import { RESPONSIVE_TYPE } from '../../../../components/molecules/Image/Responsive';
import { Image } from '../../../../types/api/ImageType';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useValdidationRules } from '../../../FileUpload/utils/useValdidationRules';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { formParamsUtils } from '../../utils/formParamsUtils';
import { RequiredHelpPhrase } from '../../partials/RequiredHelpPhrase/RequiredHelpPhrase';
import { Input } from '../Input/Input';
import { Row } from '../../grid/Row/Row';
import { NestedParam } from '../../types/NestedParam';
import { TextParam } from '../../types/TextParam';
import { ImageFileParam } from '../../types/FileUploadParam';
import { ButtonMinorAction } from '../../../../components/molecules/Btn/ButtonWrapper';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';

export type TeaserImageUploadProps = {
  fetchUrl?: string;
  param: NestedParam<
    'imageuploadparams',
    'imageupload',
    TextParam<'alt'> | TextParam<'copyright'> | ImageFileParam<'image'>
  >;
};

export function TeaserImageUpload({ param, fetchUrl }: TeaserImageUploadProps) {
  const { t } = useSonTranslation('TeaserInformation');

  const params = formParamsUtils(param);
  const image = params.getByName('image');
  const alt = params.getByName('alt');
  const copyright = params.getByName('copyright');

  const [selectedImage, setSelectedImage] = useState<SelectedImage | undefined>(undefined);
  const [backendImage, setBackendImage] = useState<typeof image.value | undefined>(image.value);
  const [altParamState, setAltParamState] = useState({ ...alt });
  const [copyrightParamState, setCopyrightParamState] = useState({ ...copyright });
  const [readingFile, setReadingFile] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(undefined);
  const labelRef = useRef<HTMLLabelElement>(undefined);
  const combinedRef = useRef<CombinedRefContent>({
    inputRef: inputRef,
    labelRef: labelRef,
  });
  const validationRules = useValdidationRules(image.constraints || {});
  const validate = useValidateImage();

  useEffect(() => {
    if (isValid(selectedImage)) {
      inputRef.current?.setCustomValidity('');
    } else {
      const errorString = Array.isArray(selectedImage?.errors)
        ? // Set() only allowing unique values makes sure we don't join duplicate error strings
          [...new Set(selectedImage?.errors)].join('\n')
        : selectedImage?.errors;
      inputRef.current?.setCustomValidity(errorString || '');
    }
  }, [selectedImage]);

  return (
    <>
      <BaseFileUpload
        ref={combinedRef as any} // Typescript misinterprets the CombinedRefContent
        param={image}
        selectButtonLabel={t('ImageUpload:buttons.chooseFile')}
        onSelectedFilesChanged={event => {
          const file = event?.target?.files?.item(0);

          if (file) {
            validate(file, validationRules)
              .then(value => setSelectedImage(value))
              .catch(() => setSelectedImage(undefined))
              .finally(() => setReadingFile(false));
          }

          labelRef.current?.focus();
          setReadingFile(true);
        }}
      >
        {selectedImage || backendImage ? (
          <div className="teaser-image-upload__delete">
            <h4 className="font-bold-15-19">{t('headline')}</h4>
            <div className="teaser-image-upload__delete-button">
              <ButtonMinorAction
                text={t('eraseImageButtonText')}
                tooltipPlacement={TooltipPlacement.TOP}
                icon={Icons.ERASER}
                type={ButtonType.RESET}
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.value = '';
                  }

                  setAltParamState({ ...altParamState, value: '' });
                  setCopyrightParamState({ ...copyrightParamState, value: '' });
                  setSelectedImage(undefined);
                  setBackendImage(undefined);
                }}
              />
            </div>
          </div>
        ) : null}
        <PreviewSuspense id={useHtmlValidId()} isLoading={readingFile} selectedPreview={selectedImage}>
          {isValid(selectedImage) ? (
            <SelectedImagePreview
              selectedPreview={selectedImage}
              responsiveType={RESPONSIVE_TYPE.teaser}
              filename={selectedImage.file.name}
              customAltText={t('Image:alternativeText.teaserImage')}
              imageType={image.value?.imageType}
            />
          ) : (
            backendImage && (
              <ImagePreview
                image={{ ...(backendImage as any), copyright: undefined } as Image}
                responsiveType={RESPONSIVE_TYPE.teaser}
                fetchUrl={fetchUrl}
                lazyLoading
              />
            )
          )}
        </PreviewSuspense>
      </BaseFileUpload>
      <RequiredHelpPhrase />
      {isValid(selectedImage) || backendImage ? (
        <>
          <Row>
            <Input
              label={t('ImageUpload:pictureDescriptionLabel')}
              helpText={t('ImageUpload:pictureDescriptionHelpText')}
              param={altParamState}
            />
          </Row>
          <Row>
            <Input
              label={t('ImageUpload:pictureCopyrightLabel')}
              helpText={t('ImageUpload:pictureCopyrightHelpText')}
              param={copyrightParamState}
            />
          </Row>
        </>
      ) : null}
    </>
  );
}
